import { gql, useMutation } from '@apollo/client';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Tooltip,
} from '@apollo/orbit';
import React, { useEffect, useRef, useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalLifecycleEvent } from 'src/lib/graphqlTypes/types';

import {
  AddEventsModal,
  AddEventsMode,
} from '../../addNotificationWizard/addEventsModal/AddEventsModal';
import { useDeleteSubscription } from '../useDeleteSubscription';

import { SendTestNotificationModal } from './SendTestNotificationModal';

export const testWebhookChannelMutation = gql`
  mutation TestWebhookChannelMutation(
    $graphId: ID!
    $channelId: ID!
    $subscriptionId: ID!
  ) {
    service(id: $graphId) {
      testSubscriptionForChannel(
        channelID: $channelId
        subscriptionID: $subscriptionId
      )
    }
  }
`;

export const queueTestProposalLifecycleNotificationMutation = gql`
  mutation QueueTestProposalLifecycleNotificationMutation(
    $input: QueueTestProposalLifecycleNotificationInput!
    $graphId: ID!
  ) {
    graph(id: $graphId) {
      queueTestProposalLifecycleNotification(input: $input) {
        ... on QueueTestProposalLifecycleNotificationError {
          message
        }
        ... on QueueTestProposalLifecycleNotificationSuccess {
          queued
        }
        ... on Error {
          message
          __typename
        }
      }
    }
  }
`;

const updateProposalLifecycleSubscriptionMutation = gql`
  mutation UpdateProposalLifecycleSubscriptionMutation(
    $input: UpdateProposalLifecycleSubscriptionInput!
    $graphId: ID!
  ) {
    graph(id: $graphId) {
      updateProposalLifecycleSubscription(input: $input) {
        ... on ProposalLifecycleSubscription {
          events
          id
        }
      }
    }
  }
`;

export function NotificationActionMenu({
  disabled,
  graphId,
  subscription,
  channel,
}: {
  disabled: boolean;
  graphId: string;
  subscription: GraphQLTypes.ChannelCardChannelFragment_subscriptions;
  channel: GraphQLTypes.ChannelCardChannelFragment;
}) {
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const focusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (deleteModalVisible && focusRef.current) {
      focusRef.current.focus();
    }
  }, [deleteModalVisible]);
  const [isAddEventsModalOpen, setIsAddEventsModalOpen] = React.useState(false);

  const [sendTestModalVisible, setSendTestModalVisible] =
    useState<boolean>(false);

  const [selectedEvents, setEvents] = useState<ProposalLifecycleEvent[]>(
    subscription.__typename === 'ProposalLifecycleSubscription'
      ? subscription.events
      : [],
  );

  const { loading, error, deleteSubscription } = useDeleteSubscription({
    graphId,
    subscription,
    onCompleted: () => setDeleteModalVisible(false),
  });

  const [updateProposalSub, { loading: loadingUpdateProposalSub }] =
    useMutation<
      GraphQLTypes.UpdateProposalLifecycleSubscriptionMutation,
      GraphQLTypes.UpdateProposalLifecycleSubscriptionMutationVariables
    >(updateProposalLifecycleSubscriptionMutation, {
      variables: {
        input: {
          id: subscription.id,
          events: selectedEvents,
        },
        graphId,
      },
      onCompleted: () => {
        setIsAddEventsModalOpen(false);
      },
    });

  if (error !== undefined) {
    throw new Error(
      'Something went wrong trying to delete your subscription...',
    );
  }

  const [
    queueTestProposalNotificationMutation,
    { loading: loadingTestNotification },
  ] = useMutation<
    GraphQLTypes.QueueTestProposalLifecycleNotificationMutation,
    GraphQLTypes.QueueTestProposalLifecycleNotificationMutationVariables
  >(queueTestProposalLifecycleNotificationMutation);

  const [testRegistrySubscription, { loading: testWebhookLoading }] =
    useMutation<
      GraphQLTypes.TestWebhookChannelMutation,
      GraphQLTypes.TestWebhookChannelMutationVariables
    >(testWebhookChannelMutation);

  // we can only test a Webhook channel if it's a RegistrySubscription, or if it's a ProposalLifecycleSubscription (although that has a different mutation)
  const canTestWebhookChannel =
    (channel.__typename === 'WebhookChannel' &&
      subscription.__typename === 'RegistrySubscription') ||
    subscription.__typename === 'ProposalLifecycleSubscription';

  return (
    <>
      <Menu>
        <Tooltip
          isDisabled={subscription.__typename !== 'QueryTrigger'}
          label="Editing not available. Manage performance alerts from the table
              above."
        >
          <MenuButton
            as={IconButton}
            icon={<IconOverflow />}
            variant="secondary"
            isDisabled={disabled || subscription.__typename === 'QueryTrigger'}
          />
        </Tooltip>
        <MenuContent>
          <MenuList>
            {canTestWebhookChannel && (
              <MenuItem
                onClick={() => {
                  if (
                    subscription.__typename === 'ProposalLifecycleSubscription'
                  ) {
                    queueTestProposalNotificationMutation({
                      variables: {
                        input: {
                          channelId: channel.id,
                          subscriptionId: subscription.id,
                        },
                        graphId,
                      },
                    });
                  }
                  if (subscription.__typename === 'RegistrySubscription') {
                    testRegistrySubscription({
                      variables: {
                        graphId,
                        channelId: channel.id,
                        subscriptionId: subscription.id,
                      },
                    });
                  }
                  setSendTestModalVisible(true);
                }}
              >
                Send test notification
              </MenuItem>
            )}
            {subscription.__typename === 'ProposalLifecycleSubscription' && (
              <MenuItem onClick={() => setIsAddEventsModalOpen(true)}>
                Edit events
              </MenuItem>
            )}
            <MenuItem
              onClick={() => setDeleteModalVisible(true)}
              className="text-error hover:bg-error hover:text-error"
            >
              Delete
            </MenuItem>
          </MenuList>
        </MenuContent>
      </Menu>

      {deleteModalVisible && (
        <Modal isOpen onClose={() => setDeleteModalVisible(false)} size="lg">
          <ModalOverlay />
          <Form
            onSubmit={() => {
              deleteSubscription();
            }}
            noValidate
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Delete Notification?</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <em>This action cannot be undone. Do you wish to continue?</em>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    ref={focusRef}
                    isDisabled={loading}
                    onClick={() => setDeleteModalVisible(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    isDisabled={loading}
                    type="submit"
                  >
                    Delete
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Form>
        </Modal>
      )}
      {sendTestModalVisible && (
        <SendTestNotificationModal
          loadingTestNotification={
            loadingTestNotification || testWebhookLoading
          }
          setSendTestModalVisible={setSendTestModalVisible}
          channel={channel}
        />
      )}
      {isAddEventsModalOpen && (
        <AddEventsModal
          isOpen={isAddEventsModalOpen}
          state={{
            notificationType: 'ProposalLifecycleSubscription',
            events: selectedEvents,
          }}
          onCheckboxChange={(events: ProposalLifecycleEvent[]) =>
            setEvents(events)
          }
          onComplete={() => {
            updateProposalSub();
          }}
          onClose={() => {
            setIsAddEventsModalOpen(false);
          }}
          mode={AddEventsMode.EDIT}
          loading={loadingUpdateProposalSub}
        />
      )}
    </>
  );
}
